import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Building2, Plus, Edit2, Trash2, ChevronUp, ChevronDown, Upload } from 'lucide-react';
import { API, resolveImg, getToken } from './shared';

const BuildersTab = () => {
  const [builders, setBuilders] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', logo: '' });
  const [uploading, setUploading] = useState(false);

  useEffect(() => { fetchBuilders(); }, []);

  const fetchBuilders = async () => {
    try {
      const res = await axios.get(`${API}/admin/builders`, { headers: { Authorization: `Bearer ${getToken()}` } });
      setBuilders(res.data);
    } catch (error) { console.error('Error fetching builders:', error); }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/upload-image`, fd);
      setForm({ ...form, logo: res.data.url });
    } catch (error) {
      console.error('Upload error:', error);
      alert('Failed to upload logo: ' + (error.response?.data?.detail || error.message));
    } finally { setUploading(false); }
  };

  const handleSave = async () => {
    if (!form.name || !form.logo) { alert('Name and logo are required'); return; }
    try {
      const config = { headers: { Authorization: `Bearer ${getToken()}` } };
      if (editing) {
        await axios.put(`${API}/admin/builders/${editing.id}`, { name: form.name, logo: form.logo }, config);
      } else {
        await axios.post(`${API}/admin/builders`, { name: form.name, logo: form.logo, order: builders.length }, config);
      }
      setShowForm(false);
      setEditing(null);
      setForm({ name: '', logo: '' });
      fetchBuilders();
    } catch (error) {
      console.error('Save error:', error);
      const detail = error.response?.data?.detail;
      let msg = 'Failed to save builder';
      if (typeof detail === 'string') msg = detail;
      else if (Array.isArray(detail)) msg = detail.map(e => e.msg || JSON.stringify(e)).join('\n');
      alert(msg);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this builder?')) return;
    try {
      await axios.delete(`${API}/admin/builders/${id}`, { headers: { Authorization: `Bearer ${getToken()}` } });
      fetchBuilders();
    } catch (error) { console.error('Delete error:', error); }
  };

  const handleMove = async (index, direction) => {
    const swapIdx = index + direction;
    if (swapIdx < 0 || swapIdx >= builders.length) return;
    const updated = [...builders];
    const [moved] = updated.splice(index, 1);
    updated.splice(swapIdx, 0, moved);
    setBuilders(updated);
    try {
      await axios.post(`${API}/admin/builders/reorder`, { ids: updated.map(b => b.id) }, { headers: { Authorization: `Bearer ${getToken()}` } });
    } catch (error) {
      console.error('Reorder error:', error);
      fetchBuilders();
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl sm:text-3xl font-semibold">Popular Builders</h2>
          <p className="text-sm text-slate-500 mt-1">Manage builder logos shown on the homepage</p>
        </div>
        <button
          onClick={() => { setEditing(null); setForm({ name: '', logo: '' }); setShowForm(true); }}
          className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition-all shadow-lg shadow-blue-500/20"
          data-testid="add-builder-btn"
        >
          <Plus className="w-5 h-5" /> Add Builder
        </button>
      </div>

      {builders.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center">
          <Building2 className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-slate-700 mb-2">No Builders Yet</h3>
          <p className="text-slate-500">Add builders to display on the homepage</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4" data-testid="builders-list">
          {builders.map((builder, idx) => (
            <div key={builder.id} className="bg-white rounded-2xl border border-slate-200 p-4 group relative" data-testid={`builder-item-${builder.id}`}>
              <div className="h-24 flex items-center justify-center mb-3">
                <img src={resolveImg(builder.logo)} alt={builder.name} className="max-h-full max-w-full object-contain" />
              </div>
              <p className="text-sm font-semibold text-slate-900 text-center truncate">{builder.name}</p>
              {/* Actions overlay */}
              <div className="absolute inset-0 rounded-2xl bg-black/0 group-hover:bg-black/5 transition-all" />
              <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <button onClick={() => handleMove(idx, -1)} disabled={idx === 0} className="w-7 h-7 rounded-lg bg-white shadow flex items-center justify-center hover:bg-slate-50 disabled:opacity-30" data-testid={`builder-up-${builder.id}`}>
                  <ChevronUp className="w-4 h-4" />
                </button>
                <button onClick={() => handleMove(idx, 1)} disabled={idx === builders.length - 1} className="w-7 h-7 rounded-lg bg-white shadow flex items-center justify-center hover:bg-slate-50 disabled:opacity-30" data-testid={`builder-down-${builder.id}`}>
                  <ChevronDown className="w-4 h-4" />
                </button>
                <button onClick={() => { setEditing(builder); setForm({ name: builder.name, logo: builder.logo }); setShowForm(true); }} className="w-7 h-7 rounded-lg bg-white shadow flex items-center justify-center hover:bg-blue-50" data-testid={`builder-edit-${builder.id}`}>
                  <Edit2 className="w-3.5 h-3.5 text-blue-600" />
                </button>
                <button onClick={() => handleDelete(builder.id)} className="w-7 h-7 rounded-lg bg-white shadow flex items-center justify-center hover:bg-red-50" data-testid={`builder-delete-${builder.id}`}>
                  <Trash2 className="w-3.5 h-3.5 text-red-500" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Builder Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <div className="bg-white rounded-2xl w-full max-w-md p-8" onClick={e => e.stopPropagation()} data-testid="builder-form-modal">
            <h3 className="text-2xl font-bold mb-6">{editing ? 'Edit Builder' : 'Add Builder'}</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Builder Name</label>
                <input type="text" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="w-full h-11 rounded-lg border border-slate-200 px-4 text-sm focus:ring-2 focus:ring-blue-100 focus:border-blue-500" placeholder="e.g., Skyline Builders" data-testid="builder-input-name" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Logo</label>
                {form.logo ? (
                  <div className="flex items-center gap-4 mb-2">
                    <div className="h-20 w-32 bg-slate-50 rounded-lg border border-slate-200 flex items-center justify-center p-2">
                      <img src={resolveImg(form.logo)} alt="Preview" className="max-h-full max-w-full object-contain" />
                    </div>
                    <button onClick={() => setForm({ ...form, logo: '' })} className="text-sm text-red-500 hover:text-red-700">Remove</button>
                  </div>
                ) : null}
                <label className={`flex items-center justify-center gap-2 h-11 rounded-lg border border-dashed border-slate-300 text-sm font-medium cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-all ${uploading ? 'opacity-50 pointer-events-none' : 'text-slate-600'}`}>
                  {uploading ? (
                    <><div className="w-4 h-4 border-2 border-slate-300 border-t-blue-600 rounded-full animate-spin" /> Uploading...</>
                  ) : (
                    <><Upload className="w-4 h-4" /> {form.logo ? 'Change Logo' : 'Upload Logo'}</>
                  )}
                  <input type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" data-testid="builder-logo-input" />
                </label>
              </div>
              <div className="flex gap-3 pt-4">
                <button onClick={handleSave} disabled={!form.name || !form.logo} className="flex-1 h-11 rounded-lg bg-blue-600 text-white font-medium hover:bg-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed" data-testid="builder-save-btn">
                  {editing ? 'Update' : 'Add Builder'}
                </button>
                <button onClick={() => setShowForm(false)} className="h-11 px-6 rounded-lg bg-slate-100 text-slate-700 font-medium hover:bg-slate-200 transition-all">Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BuildersTab;
